"use client";

import Link from "next/link";
import { useAuth } from "@/lib/auth";
import { usePathname } from "next/navigation";
import { NAV_LABELS } from "@/lib/labels";

const PUBLIC_PATHS = ["/login"];

/* ------------------------------------------------------------------ */
/*  Navigation structure                                               */
/* ------------------------------------------------------------------ */

interface NavItem {
  label: string;
  href: string;
}

interface NavGroup {
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: NAV_LABELS.quotations, href: "/quotations" },
      { label: NAV_LABELS.proposals, href: "/proposals" },
      { label: NAV_LABELS.orders, href: "/orders" },
      { label: NAV_LABELS.customers, href: "/customers" },
    ],
  },
  {
    items: [
      { label: NAV_LABELS.machines, href: "/machines" },
      { label: NAV_LABELS.materials, href: "/materials" },
      { label: NAV_LABELS.profiles, href: "/profiles" },
    ],
  },
  {
    items: [{ label: NAV_LABELS.settings, href: "/settings" }],
  },
];

/**
 * App shell — wraps all pages. Redirects to /login if not authenticated.
 * Public paths (login) render without auth check.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, loading, logout } = useAuth();
  const pathname = usePathname();

  const isPublic = PUBLIC_PATHS.some((p) => pathname.startsWith(p));

  // Public pages render immediately
  if (isPublic) return <>{children}</>;

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--color-background)",
          color: "var(--color-muted)",
          fontSize: "14px",
        }}
      >
        Loading...
      </div>
    );
  }

  // Not authenticated — redirect
  if (!user) {
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    return null;
  }

  const isActive = (href: string): boolean =>
    pathname === href || pathname.startsWith(href + "/");

  // Authenticated — render with nav bar
  return (
    <>
      {/* Top navigation bar */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          left: 0,
          height: "48px",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          background: "linear-gradient(135deg, #00205B, #003DA5)",
          zIndex: 1000,
          gap: "0",
        }}
      >
        {/* Logo / Home */}
        <Link
          href="/"
          style={{
            fontSize: "15px",
            fontWeight: 700,
            color: "#fff",
            textDecoration: "none",
            marginRight: "24px",
            letterSpacing: "-0.3px",
          }}
        >
          Quotes
        </Link>

        {/* Nav groups separated by subtle dividers */}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "0" }}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} style={{ display: "flex", alignItems: "center" }}>
              {gi > 0 && (
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    background: "rgba(255,255,255,0.2)",
                    margin: "0 4px",
                  }}
                />
              )}
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    style={{
                      fontSize: "13px",
                      fontWeight: active ? 600 : 400,
                      color: active ? "#fff" : "rgba(255,255,255,0.75)",
                      textDecoration: "none",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      background: active ? "rgba(255,255,255,0.15)" : "transparent",
                      transition: "background 0.15s, color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* User info + sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>
            {user.name} ({user.role})
          </span>
          <button
            onClick={logout}
            style={{
              padding: "4px 12px",
              fontSize: "11px",
              fontWeight: 600,
              background: "rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.85)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </nav>
      {/* Content with top padding for nav bar */}
      <div style={{ paddingTop: "48px" }}>{children}</div>
    </>
  );
}
